import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import SessionExpiredModal from '../components/SessionExpiredModal';
import { onSessionTerminated, offSessionTerminated } from '../utils/socket';
import { idbClearAll } from '../utils/idbCache';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState('');
  const [sessionExpiredRole, setSessionExpiredRole] = useState(null); // save role before user is cleared
  const logoutInProgressRef = useRef(false); // prevents session validation from showing modal during manual logout

  useEffect(() => {
    // Load user from localStorage
    const savedUser = localStorage.getItem('dws_user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  // Heartbeat: Update last_seen every 30 seconds
  useEffect(() => {
    if (!user?.id) return;

    const updateLastSeen = async () => {
      try {
        await axios.post(`${API}/admin/activity/track`, {
          user_id: user.id,
          action: 'heartbeat'
        });
      } catch (error) {
        console.error('Heartbeat failed:', error);
      }
    };

    // Initial heartbeat
    updateLastSeen();

    // Set interval for periodic updates
    const interval = setInterval(updateLastSeen, 30000); // Every 30 seconds

    return () => clearInterval(interval);
  }, [user]);

  // Track logout when user closes browser/tab
  useEffect(() => {
    const handleBeforeUnload = async () => {
      if (user?.id) {
        // Send logout tracking (use sendBeacon for reliable delivery)
        const data = JSON.stringify({
          user_id: user.id,
          action: 'logout'
        });
        navigator.sendBeacon(`${API}/admin/activity/track`, data);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [user]);

  const login = (userData) => {
    setUser(userData);
    localStorage.setItem('dws_user', JSON.stringify(userData));
    // Store session_id separately
    if (userData.session_id) {
      localStorage.setItem('dws_session_id', userData.session_id);
    }
  };

  const logout = async (isManual = true) => {
    logoutInProgressRef.current = true; // Block session validation modal immediately
    
    // Track logout before clearing user
    if (user?.id) {
      try {
        await axios.post(`${API}/admin/activity/track`, {
          user_id: user.id,
          action: 'logout'
        });
        
        // Delete session from backend
        const sessionId = localStorage.getItem('dws_session_id');
        if (sessionId) {
          await axios.post(`${API}/auth/logout`, null, {
            params: { session_id: sessionId }
          });
        }
      } catch (error) {
        console.error('Logout tracking failed:', error);
      }
    }
    
    // Clear local state and storage
    const userRole = user?.role;
    setUser(null);
    localStorage.removeItem('dws_user');
    localStorage.removeItem('dws_session_id');
    // Offline-mode cache cleanup — drop the persistent chat-list caches so a
    // different user on the same device never sees the previous user's chats.
    try {
      localStorage.removeItem('hdl_chats');
      localStorage.removeItem('hdl_chats_by_type');
      localStorage.removeItem('hdl_groups');
      localStorage.removeItem('dws_instant_cache_admin_rooms_self');
      localStorage.removeItem('dws_instant_cache_admin_groups_self');
      localStorage.removeItem('dws_list_width');
    } catch (_) { /* ignore */ }
    // Point 2 fix — clear staff per-tab UI-state (sessionStorage) so a
    // DIFFERENT staff user logging in on the SAME browser tab doesn't inherit
    // the previous user's last tab / filter / scroll positions. These are
    // cosmetic UI-state keys only (NOT device-trust / 2FA / password-reset
    // tokens, which are deliberately left untouched).
    try {
      const staffUiStateKeys = [
        'admin_active_tab', 'admin_filter_tab', 'admin_chat_list_scroll',
        'admin_tab_before_archive', 'admin_settings_grid_scroll', 'admin-mb-filter',
        'hdl_active_tab', 'hdl_chat_type_tab', 'handler_chat_list_scroll',
        'hdl_tab_before_archive', 'handler-mb-filter',
      ];
      staffUiStateKeys.forEach(k => { try { sessionStorage.removeItem(k); } catch (_) {} });
      // Dynamic-suffix families (per-tab list scroll + service-timeline filters).
      Object.keys(sessionStorage).forEach(k => {
        if (k.startsWith('admin_list_scroll_') || k.startsWith('svc_tl_')) {
          try { sessionStorage.removeItem(k); } catch (_) {}
        }
      });
    } catch (_) { /* ignore */ }
    // Wipe the durable IndexedDB chat cache too, so a different admin on the
    // same device never sees the previous admin's saved chats.
    try { idbClearAll(); } catch (_) { /* ignore */ }
    
    // Redirect based on logout type
    if (isManual) {
      // Manual logout - redirect immediately without popup
      const loginRoute = userRole === 'customer' ? '/customer-login' 
        : userRole === 'super_admin' ? '/admin-login' 
        : ['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'calling_handler'].includes(userRole) ? '/handler-login'
        : '/customer-login';
      window.location.href = loginRoute;
    }
    // For forced logout (session expired), modal will handle redirect
  };

  // Session validation polling (every 15 seconds)
  useEffect(() => {
    if (!user?.id) return;
    if (user?.is_guest) return; // Skip session validation for guest support users
    if (user?.support_restricted) return; // Device-recovery (Trouble Sign In) restricted sessions are exempt until a real password login

    const validateSession = async () => {
      const sessionId = localStorage.getItem('dws_session_id');
      if (!sessionId) {
        if (logoutInProgressRef.current || !localStorage.getItem('dws_user')) return;
        setSessionExpiredRole(user?.role);
        await logout(false);
        setSessionExpiredMessage('Session not found. Please login again.');
        setShowSessionExpired(true);
        return;
      }

      try {
        const response = await axios.post(`${API}/auth/validate-session`, null, {
          params: { session_id: sessionId }
        });

        if (!response.data.valid) {
          if (logoutInProgressRef.current || !localStorage.getItem('dws_user')) return;
          setSessionExpiredRole(user?.role);
          await logout(false);
          setSessionExpiredMessage('Your session has expired or you have logged in from another device.');
          setShowSessionExpired(true);
        }
      } catch (error) {
        console.error('Session validation failed:', error);
      }
    };

    // Validate immediately on mount
    validateSession();

    // Then validate every 15 seconds
    const interval = setInterval(validateSession, 15000);

    return () => clearInterval(interval);
  }, [user]);

  // Proactive kick notice: react to `session_terminated` the moment the
  // backend evicts THIS session (2-concurrent-session cap), instead of
  // waiting up to 15s for the next poll to notice. Named reason comes
  // straight from the backend so the admin/handler sees exactly which
  // session/device caused it.
  useEffect(() => {
    if (!user?.id) return;
    if (user?.is_guest) return;
    if (user?.support_restricted) return;

    const handleTerminated = async (payload) => {
      const mySessionId = localStorage.getItem('dws_session_id');
      if (!mySessionId || payload?.session_id !== mySessionId) return; // not this session
      if (logoutInProgressRef.current) return;
      setSessionExpiredRole(user?.role);
      await logout(false);
      setSessionExpiredMessage(payload?.reason || 'You were signed out on this device.');
      setShowSessionExpired(true);
    };

    onSessionTerminated(handleTerminated);
    return () => offSessionTerminated(handleTerminated);
  }, [user]);

  const updateUser = (updates) => {
    const updated = { ...user, ...updates };
    setUser(updated);
    localStorage.setItem('dws_user', JSON.stringify(updated));
  };

  // P2 — re-fetch the current customer's auth flags so Deposit/Withdraw
  // buttons appear the instant the backend flips is_guest / is_inquiry /
  // support_restricted (e.g. after the guest registers / is assigned an MB),
  // without forcing a full re-login. No-ops for pure guests (id not a real
  // customer → 404) so nothing breaks.
  const refreshUser = async () => {
    if (!user?.id) return;
    try {
      const res = await axios.get(`${API}/customers/profile/${user.id}`);
      const c = res.data?.customer;
      if (!c) return;
      const nextGuest = c.is_guest ?? false;
      const nextInquiry = c.is_inquiry ?? false;
      const nextRestricted = c.support_restricted ?? false;
      // Only update when an auth-relevant flag actually changed — avoids a
      // needless re-render loop when nothing flipped.
      if (nextGuest === !!user.is_guest && nextInquiry === !!user.is_inquiry
          && nextRestricted === !!user.support_restricted
          && (c.mb_setup ?? null) === (user.mb_setup ?? null)) {
        return;
      }
      const merged = {
        ...user,
        is_guest: nextGuest,
        is_inquiry: nextInquiry,
        support_restricted: nextRestricted,
        mb_setup: c.mb_setup ?? user.mb_setup,
        client_id: c.client_id ?? user.client_id,
        personal_name: c.personal_name ?? user.personal_name,
        role: c.role || user.role,
      };
      setUser(merged);
      localStorage.setItem('dws_user', JSON.stringify(merged));
    } catch (_) { /* guest / transient — keep current */ }
  };

  const handleSessionExpiredClose = () => {
    setShowSessionExpired(false);
    // Use saved role (captured before user was cleared) for correct redirect
    const userRole = sessionExpiredRole || user?.role || 'customer';
    const loginRoute = userRole === 'customer' ? '/customer-login' 
      : userRole === 'super_admin' ? '/admin-login' 
      : ['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler'].includes(userRole) ? '/handler-login'
      : '/customer-login';
    setSessionExpiredRole(null);
    window.location.href = loginRoute;
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUser, refreshUser, loading }}>
      {children}
      <SessionExpiredModal 
        show={showSessionExpired}
        message={sessionExpiredMessage}
        onClose={handleSessionExpiredClose}
      />
    </AuthContext.Provider>
  );
};
